import Color from 'color';

/**
 * Generates an array of color variations from a base color
 *
 * Creates lighter and darker variations of the base color for pie chart segments.
 * The variations are evenly distributed to ensure good visual distinction.
 *
 * @param {string} baseColor - Hex color string (e.g., '#F04E4A')
 * @param {number} count - Number of color variations to generate
 * @returns {string[]} Array of hex color strings
 *
 * @example
 * generateColorVariations('#F04E4A', 5)
 * // Returns: ['#F04E4A', '#F26B67', '#F48884', '#F6A5A1', '#F8C2BE']
 */
export const generateColorVariations = (baseColor: string, count: number): string[] => {
  if (count <= 0) {
    return [];
  }

  if (count === 1) {
    return [baseColor];
  }

  try {
    const color = Color(baseColor);
    const variations: string[] = [];

    // For 2-3 colors: use base, lighter, and lightest
    if (count <= 3) {
      variations.push(baseColor);
      if (count >= 2) {
        variations.push(color.lighten(0.2).hex());
      }
      if (count >= 3) {
        variations.push(color.lighten(0.4).hex());
      }
      return variations;
    }

    // For 4+ colors: use mix of lighter and darker variations
    const step = 0.15;
    const halfCount = Math.floor(count / 2);

    // Add darker variations first (for even distribution)
    for (let i = halfCount - 1; i >= 0; i--) {
      variations.push(color.darken(step * (halfCount - i)).hex());
    }

    // Add base color
    variations.push(baseColor);

    // Add lighter variations
    for (let i = 1; i <= count - halfCount - 1; i++) {
      variations.push(color.lighten(step * i).hex());
    }

    return variations.slice(0, count);
  } catch (error) {
    // Fallback: return base color repeated if color parsing fails
    return Array(count).fill(baseColor);
  }
};
